import {
  Box,
  Button,
  Heading,
  Image,
  VStack,
  HStack,
  Input,
  FormControl,
  FormLabel,
  IconButton,
  Tooltip,
  Flex,
  useToast,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Code,
} from "@chakra-ui/react";

import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import CodesetSelect from "./CodesetSelect";
import UuidPreview from "./UuidPreview";
import {
  HiOutlineArrowNarrowLeft,
  HiOutlinePlus,
  HiOutlineSave,
  HiX,
  HiOutlineTrash,
} from "react-icons/hi";
import Navbar from "./Navbar";
import HeroImage from "./images/stamps.png";
import ReferencesEditor from "./ReferencesEditor";
import ClassificationsEditor from "./ClassificationsEditor";
import AmountsEditor from "./AmountsEditor";
import DateTimesEditor from "./DateTimesEditor";
import { debounce } from "lodash";

const RegisterEntryBlock = React.memo(({ entry, idx, onChange, onDelete }) => {
  const handleFieldChange = (field, value) => {
    const updated = { ...entry, [field]: value };
    onChange(updated);
  };

  return (
    <Box
      key={idx}
      borderWidth="1px"
      borderRadius="md"
      p={4}
      mb={4}
      borderColor="gray.500"
      backgroundColor="gray.200">
      <FormControl>
        <Input
          fontSize="xl"
          fontWeight="bold"
          placeholder="Enter an entry name..."
          value={entry.name || ""}
          onChange={(e) => handleFieldChange("name", e.target.value)}
        />
      </FormControl>
      <Tabs orientation="horizontal" variant="line" colorScheme="linkedin">
        <TabList>
          <Tab>Header</Tab>
          <Tab>References</Tab>
          <Tab>Classifications</Tab>
          <Tab>Amounts</Tab>
          <Tab>DateTimes</Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            <CodesetSelect
              label="Type"
              codesetCode="B58"
              value={entry.type ? { code: entry.type, description: "" } : null}
              onChange={(val) => handleFieldChange("type", val.code)}
            />

            <FormControl>
              <FormLabel>Description</FormLabel>
              <Input
                value={entry.description || ""}
                onChange={(e) =>
                  handleFieldChange("description", e.target.value)
                }
              />
            </FormControl>
          </TabPanel>
          <TabPanel>
            <ReferencesEditor
              size="xs"
              references={entry.references || []}
              onChange={(refs) => handleFieldChange("references", refs)}
            />
          </TabPanel>
          <TabPanel>
            <ClassificationsEditor
              size="xs"
              classifications={entry.classifications || []}
              onChange={(classifications) =>
                handleFieldChange("classifications", classifications)
              }
            />
          </TabPanel>
          <TabPanel>
            <AmountsEditor
              size="xs"
              amounts={entry.amounts || []}
              onChange={(amounts) => handleFieldChange("amounts", amounts)}
            />
          </TabPanel>
          <TabPanel>
            <DateTimesEditor
              size="xs"
              dateTimes={entry.dateTimes || []}
              onChange={(dateTimes) =>
                handleFieldChange("dateTimes", dateTimes)
              }
            />
          </TabPanel>
        </TabPanels>
      </Tabs>

      <HStack justify="flex-end">
        <IconButton icon={<HiOutlineTrash />} size="md" onClick={onDelete} />
      </HStack>
    </Box>
  );
});

const RegistersEditor = ({ register, onClose, isNew }) => {
  const [registerHeader, setRegisterHeader] = useState({});
  const [entries, setEntries] = useState([]);
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    if (register) {
      setRegisterHeader({ ...register, entries: undefined });
      setEntries(register.entries || []);
    }
  }, [register]);

  const handleHeaderChange = useMemo(
    () =>
      debounce((field, value) => {
        setRegisterHeader((prev) => {
          if (prev[field] === value) return prev;
          return { ...prev, [field]: value };
        });
      }, 300),
    []
  );

  const handleSave = async () => {
    try {
      const fullRegister = { ...registerHeader, entries };
      const response = await fetch(
        isNew
          ? "https://registers.poc.mrc.ai/api/registers"
          : `https://registers.poc.mrc.ai/api/registers/${registerHeader._id}`,
        {
          method: isNew ? "POST" : "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(fullRegister),
        }
      );

      if (!response.ok) {
        throw new Error(
          `Failed to ${isNew ? "create" : "save"} register. Status: ${
            response.status
          }`
        );
      }

      const result = await response.json();

      toast({
        title: isNew ? "Register created" : "Register saved",
        description: isNew
          ? "New register was added."
          : "Changes saved successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      if (isNew && result._id) {
        navigate(`/registers/${result._id}`);
      }
    } catch (error) {
      console.error("❌ Error saving register:", error);

      toast({
        title: "Save failed",
        description:
          error.message || "An error occurred while saving the register.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const handleClose = () => {
    navigate("/Poc");
  };

  return (
    <Box>
      <Navbar />
      <Box position="relative" width="100%" minHeight="100vh">
        <Image
          src={HeroImage}
          alt="Hero Watermark"
          width="100%"
          height="100%"
          objectFit="cover"
          opacity={0.35}
          position="absolute"
          top={0}
          left={0}
          zIndex={-1}
          filter="blur(1px)"
        />

        <Box position="relative" zIndex={1} p={10}>
          <Box
            borderWidth="1px"
            borderRadius="md"
            p={1}
            mb={2}
            borderColor="gray.500"
            backgroundColor="whiteAlpha.800">
            <Flex mb={2}>
              <HStack spacing={6}>
                <Tooltip
                  label="Back to the Environment Homepage"
                  aria-label="Back To Environment tooltip">
                  <IconButton
                    icon={<HiOutlineArrowNarrowLeft />}
                    size="md"
                    onClick={handleClose}
                  />
                </Tooltip>
                <Heading size="lg">Edit Register</Heading>
                <Tooltip
                  label="Save Register"
                  aria-label="Save Register Tooltip">
                  <IconButton
                    icon={<HiOutlineSave />}
                    size="md"
                    onClick={handleSave}
                  />
                </Tooltip>
              </HStack>
            </Flex>
          </Box>

          <VStack spacing={6} align="stretch">
            <Box
              borderWidth="1px"
              borderRadius="md"
              p={4}
              borderColor="gray.500"
              backgroundColor="whiteAlpha.800">
              <UuidPreview uuid={registerHeader._id} />
              <FormControl>
                <FormLabel>Name</FormLabel>
                <Input
                  value={registerHeader.name || ""}
                  onChange={(e) => handleHeaderChange("name", e.target.value)}
                />
              </FormControl>

              <CodesetSelect
                label="Type"
                codesetCode="B57"
                value={
                  registerHeader.type
                    ? { code: registerHeader.type, description: "" }
                    : null
                }
                onChange={(val) => handleHeaderChange("type", val.code)}
              />

              <FormControl>
                <FormLabel>Description</FormLabel>
                <Input
                  value={registerHeader.description || ""}
                  onChange={(e) =>
                    handleHeaderChange("description", e.target.value)
                  }
                />
              </FormControl>
              <FormControl mt={4}>
                <FormLabel>As Of Date</FormLabel>
                <Input
                  value={registerHeader.asOfDateTime || ""}
                  onChange={(e) =>
                    handleHeaderChange("asOfDateTime", e.target.value)
                  }
                />
              </FormControl>
            </Box>

            <Box
              borderWidth="1px"
              borderRadius="md"
              p={4}
              borderColor="gray.500"
              backgroundColor="whiteAlpha.800">
              <HStack alignItems="center">
                <Heading size="md">Entries</Heading>
                <IconButton
                  icon={<HiOutlinePlus />}
                  size="md"
                  onClick={() =>
                    setEntries([...entries, { name: "", description: "" }])
                  }
                />
              </HStack>
              <Box mt={2}>
                {entries.map((entry, idx) => (
                  <RegisterEntryBlock
                    key={idx}
                    entry={entry}
                    idx={idx}
                    onChange={(updated) => {
                      const newEntries = [...entries];
                      newEntries[idx] = updated;
                      setEntries(newEntries);
                    }}
                    onDelete={() => {
                      const newEntries = [...entries];
                      newEntries.splice(idx, 1);
                      setEntries(newEntries);
                    }}
                  />
                ))}
              </Box>
            </Box>
          </VStack>
        </Box>
      </Box>
    </Box>
  );
};

export default RegistersEditor;
